/**
 * EventTypesPage.tsx — Billing Event Type Registry
 *
 * Displays and manages the canonical billing event types. Event types are pure
 * definitions of "what happened" — they do NOT contain pricing. Pricing is
 * defined in pricing catalogs (UsagePricingPage).
 *
 * Each event type defines:
 *   - A unique key (e.g. 'atp_execution')
 *   - Display name and unit of measurement
 *   - Required metadata schema (JSON Schema for validation)
 *   - Aggregation key (groups events into billing line items)
 *
 * @since 2026-06-08
 */
import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import SlidePanel from '../components/SlidePanel';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';

interface EventType {
  id: string;
  event_type: string;
  display_name: string;
  unit: string;
  description: string | null;
  is_active: boolean;
  required_metadata_schema: Record<string, unknown> | null;
  aggregation_key: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

function useEventTypes() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<EventType[]>({
    queryKey: ['admin-event-types'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-event-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!token,
  });
}

/** Fetch pricing catalog items to show which event types have pricing configured */
function usePricingCatalogItems() {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<Array<{ event_type: string; pricing_mode: string; catalog_name: string }>>({
    queryKey: ['pricing-catalog-items'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-catalog-items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.data ?? data ?? [];
    },
    enabled: !!token,
  });
}

function useCreateEventType() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { event_type: string; display_name: string; unit: string; description?: string; aggregation_key?: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-event-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Create failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-event-types'] }),
  });
}

function useUpdateEventType() {
  const { token, apiBaseUrl } = useAdminAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<EventType> & { id: string }) => {
      const res = await fetch(`${apiBaseUrl}/admin/usage-event-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-event-types'] }),
  });
}

// ---------------------------------------------------------------------------
// Event Type Detail / Edit Panel
// ---------------------------------------------------------------------------

function EventTypeDetailPanel({
  eventType,
  onClose,
  isNew,
}: {
  eventType: Partial<EventType> & { id?: string };
  onClose: () => void;
  isNew?: boolean;
}) {
  const update = useUpdateEventType();
  const create = useCreateEventType();
  const [displayName, setDisplayName] = useState(eventType.display_name ?? '');
  const [eventKey, setEventKey] = useState(eventType.event_type ?? '');
  const [unit, setUnit] = useState(eventType.unit ?? 'request');
  const [description, setDescription] = useState(eventType.description ?? '');
  const [aggregationKey, setAggregationKey] = useState(eventType.aggregation_key ?? '');
  const [isActive, setIsActive] = useState(eventType.is_active ?? true);
  const [saving, setSaving] = useState(false);

  // Editable metadata schema fields
  const initialSchema = eventType.required_metadata_schema as Record<string, unknown> | null;
  const initialProperties = (initialSchema?.properties ?? {}) as Record<string, Record<string, unknown>>;
  const initialRequired = (initialSchema?.required ?? []) as string[];
  const [schemaFields, setSchemaFields] = useState<Array<{
    name: string;
    type: string;
    description: string;
    required: boolean;
  }>>(
    Object.entries(initialProperties).map(([name, def]) => ({
      name,
      type: String(def.type ?? 'string'),
      description: String(def.description ?? ''),
      required: initialRequired.includes(name),
    })),
  );

  function addSchemaField(): void {
    setSchemaFields([...schemaFields, { name: '', type: 'string', description: '', required: false }]);
  }

  function removeSchemaField(index: number): void {
    setSchemaFields(schemaFields.filter((_, i) => i !== index));
  }

  function updateSchemaField(index: number, field: keyof typeof schemaFields[0], value: string | boolean): void {
    setSchemaFields(schemaFields.map((f, i) => i === index ? { ...f, [field]: value } : f));
  }

  function buildSchema(): Record<string, unknown> | null {
    const validFields = schemaFields.filter(f => f.name.trim());
    if (validFields.length === 0) return null;
    const properties: Record<string, unknown> = {};
    const required: string[] = [];
    for (const f of validFields) {
      const fieldDef: Record<string, unknown> = { type: f.type, description: f.description };
      if (f.type === 'uuid' || f.name.endsWith('_id')) {
        fieldDef.type = 'string';
        fieldDef.format = 'uuid';
      }
      properties[f.name] = fieldDef;
      if (f.required) required.push(f.name);
    }
    return { type: 'object', properties, ...(required.length > 0 ? { required } : {}) };
  }

  async function handleSave() {
    setSaving(true);
    try {
      const schema = buildSchema();
      if (isNew) {
        await create.mutateAsync({
          event_type: eventKey,
          display_name: displayName,
          unit,
          description: description || undefined,
          aggregation_key: aggregationKey || undefined,
          required_metadata_schema: schema,
        } as { event_type: string; display_name: string; unit: string; description?: string; aggregation_key?: string; required_metadata_schema?: Record<string, unknown> | null });
      } else {
        await update.mutateAsync({
          id: eventType.id!,
          display_name: displayName,
          unit,
          description: description || null,
          aggregation_key: aggregationKey || null,
          is_active: isActive,
          required_metadata_schema: schema,
        } as Partial<EventType> & { id: string });
      }
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <SlidePanel open={true} title={isNew ? 'New Event Type' : displayName} onClose={onClose}>
      <div className="space-y-6 p-6">
        {/* Key (readonly after creation) */}
        <div>
          <label className="block text-sm font-medium text-slate-700">Event Type Key</label>
          {isNew ? (
            <input
              type="text"
              value={eventKey}
              onChange={(e) => setEventKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="e.g. atp_execution"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
            />
          ) : (
            <p className="mt-1 font-mono text-sm text-slate-500">{eventType.event_type}</p>
          )}
          <p className="mt-1 text-xs text-slate-400">Unique identifier used when reporting events via API.</p>
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700">Display Name</label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="ATP Execution"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {/* Unit */}
        <div>
          <label className="block text-sm font-medium text-slate-700">Unit of Measurement</label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="request">request</option>
            <option value="tokens">tokens</option>
            <option value="file">file</option>
            <option value="minute">minute</option>
            <option value="message">message</option>
            <option value="cents">cents (currency burn-down)</option>
            <option value="unit">unit (generic)</option>
          </select>
          <p className="mt-1 text-xs text-slate-400">What is being counted for this event type.</p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-slate-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What this event type represents..."
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {/* Aggregation Key */}
        <div>
          <label className="block text-sm font-medium text-slate-700">Aggregation Key</label>
          <input
            type="text"
            value={aggregationKey}
            onChange={(e) => setAggregationKey(e.target.value)}
            placeholder="e.g. atp_id"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
          />
          <p className="mt-1 text-xs text-slate-400">
            Metadata property used to group multiple events into a single billing line item.
          </p>
        </div>

        {/* Active toggle (not for new) */}
        {!isNew && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsActive(!isActive)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isActive ? 'bg-cyan-500' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-slate-700">{isActive ? 'Active — events can be reported' : 'Inactive — events will be rejected'}</span>
          </div>
        )}

        {/* Editable Metadata Schema */}
        <section>
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Metadata Schema Fields</h3>
            <button
              type="button"
              onClick={addSchemaField}
              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              + Add Field
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-400">Define the expected shape of the metadata payload for this event type.</p>
          {schemaFields.length === 0 && (
            <p className="mt-2 text-xs text-slate-400">No metadata fields defined. Click "Add Field" to create one.</p>
          )}
          {schemaFields.length > 0 && (
            <div className="mt-2 space-y-2">
              {schemaFields.map((field, index) => (
                <div key={index} className="flex items-start gap-2 rounded-lg border border-slate-200 p-2">
                  <input
                    type="text"
                    value={field.name}
                    onChange={(e) => updateSchemaField(index, 'name', e.target.value)}
                    placeholder="field_name"
                    className="w-32 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                  />
                  <select
                    value={field.type}
                    onChange={(e) => updateSchemaField(index, 'type', e.target.value)}
                    className="w-24 rounded border border-slate-300 px-1 py-1 text-xs"
                  >
                    <option value="string">string</option>
                    <option value="integer">integer</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                    <option value="uuid">uuid</option>
                  </select>
                  <input
                    type="text"
                    value={field.description}
                    onChange={(e) => updateSchemaField(index, 'description', e.target.value)}
                    placeholder="Description"
                    className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                  <label className="flex items-center gap-1 pt-1">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateSchemaField(index, 'required', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs text-slate-500">Req</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => removeSchemaField(index)}
                    className="pt-1 text-xs text-red-400 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Save */}
        <div className="flex gap-3 border-t border-slate-200 pt-4">
          <button
            onClick={handleSave}
            disabled={saving || (!isNew && !displayName) || (isNew && (!eventKey || !displayName))}
            className="rounded-full bg-cyan-400 px-6 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isNew ? 'Create Event Type' : 'Save Changes'}
          </button>
          <button onClick={onClose} className="rounded-full border border-slate-300 px-6 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
        </div>
      </div>
    </SlidePanel>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function EventTypesPage(): React.JSX.Element {
  const { data: types, isLoading } = useEventTypes();
  const { data: catalogItems } = usePricingCatalogItems();
  const [selectedType, setSelectedType] = useState<EventType | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Build a map of event_type → catalog names for tag display
  const pricingMap = new Map<string, string[]>();
  for (const item of catalogItems ?? []) {
    const existing = pricingMap.get(item.event_type) ?? [];
    if (!existing.includes(item.catalog_name)) {
      existing.push(item.catalog_name);
    }
    pricingMap.set(item.event_type, existing);
  }

  const activeTypes = (types ?? []).filter((t) => t.is_active);
  const inactiveTypes = (types ?? []).filter((t) => !t.is_active);

  return (
    <AdminLayout title="Event Types">
      {isLoading && <p className="text-sm text-slate-500">Loading event types...</p>}

      {!isLoading && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-500">
                Event types define <em>what</em> billable actions exist. Pricing is configured in{' '}
                <span className="font-medium text-cyan-700">Pricing Catalogs</span>.
              </p>
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
            >
              + New Event Type
            </button>
          </div>

          {/* Event Types List */}
          <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/90 shadow-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Event Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Aggregation</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Pricing</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">Metadata</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600"></th>
                </tr>
              </thead>
              <tbody>
                {[...activeTypes, ...inactiveTypes].map((et) => {
                  const schema = et.required_metadata_schema as Record<string, unknown> | null;
                  const fieldCount = schema?.properties ? Object.keys(schema.properties as object).length : 0;
                  return (
                    <tr key={et.id} className={`border-t border-slate-100 ${!et.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{et.display_name}</p>
                        <p className="font-mono text-[11px] text-slate-400">{et.event_type}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{et.unit}</span>
                      </td>
                      <td className="px-4 py-3">
                        {et.aggregation_key
                          ? <span className="rounded bg-violet-50 px-1.5 py-0.5 font-mono text-[11px] text-violet-700">{et.aggregation_key}</span>
                          : <span className="text-xs text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {(pricingMap.get(et.event_type) ?? []).length > 0
                          ? (
                            <div className="flex flex-wrap gap-1">
                              {(pricingMap.get(et.event_type) ?? []).map((cat) => (
                                <span key={cat} className="rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-medium text-cyan-700">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          )
                          : <span className="text-xs text-slate-300">No pricing</span>}
                      </td>
                      <td className="px-4 py-3">
                        {fieldCount > 0
                          ? <span className="text-xs text-slate-600">{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
                          : <span className="text-xs text-slate-300">None</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${et.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500'}`}>
                          {et.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelectedType(et)}
                          className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {(types ?? []).length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-slate-400">No event types defined yet.</div>
            )}
          </div>
        </div>
      )}

      {/* Detail/Edit panel */}
      {selectedType && (
        <EventTypeDetailPanel
          eventType={selectedType}
          onClose={() => setSelectedType(null)}
        />
      )}

      {/* Create panel */}
      {showCreate && (
        <EventTypeDetailPanel
          eventType={{}}
          onClose={() => setShowCreate(false)}
          isNew
        />
      )}
    </AdminLayout>
  );
}
